import { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@expertloop/i18n/react";
import { useAuthStore } from "../stores/authStore";
import { translationsApi } from "../features/translations/api";
import { useTranslationLocales } from "../features/translations/useTranslationLocales";
import type { TranslationLocaleSummary } from "../features/translations/types";
import { ConfirmDialog } from "../components/ConfirmDialog";
import "./TranslationsPage.css";

export function TranslationsPage() {
	const { t } = useI18n();
	const admin = useAuthStore((s) => s.admin);
	const { data, isLoading, error, reload } = useTranslationLocales();
	const [newLocale, setNewLocale] = useState("");
	const [actionError, setActionError] = useState("");
	const [busyLocale, setBusyLocale] = useState<string | null>(null);
	const [pendingDeletion, setPendingDeletion] = useState<string | null>(null);

	const isSuperAdmin = admin?.role === "super_admin";

	const handleAddLocale = async (event: React.FormEvent) => {
		event.preventDefault();
		if (!newLocale.trim()) return;
		setActionError("");
		try {
			await translationsApi.addLocale(newLocale.trim());
			setNewLocale("");
			await reload();
		} catch {
			setActionError(t("adminI18n.addLocaleFailed"));
		}
	};

	const handleRetranslate = async (locale: string, force: boolean) => {
		setActionError("");
		setBusyLocale(locale);
		try {
			await translationsApi.retranslate(locale, force ? { force: true } : {});
			await reload();
		} catch {
			setActionError(t("adminI18n.retranslateFailed"));
		} finally {
			setBusyLocale(null);
		}
	};

	const handleDelete = async () => {
		if (!pendingDeletion) return;
		setActionError("");
		try {
			await translationsApi.deleteLocale(pendingDeletion);
			setPendingDeletion(null);
			await reload();
		} catch {
			setActionError(t("adminI18n.deleteLocaleFailed"));
			setPendingDeletion(null);
		}
	};

	if (isLoading) {
		return <div className="loading">{t("adminI18n.loading")}</div>;
	}

	if (error) {
		return <div className="error-message">{t("adminI18n.loadFailed")}</div>;
	}

	return (
		<div className="translations-page">
			<header className="content-header">
				<h1>{t("adminI18n.title")}</h1>
				<p className="subtitle">{t("adminI18n.subtitle")}</p>
			</header>

			<div className="translations-source">
				<span className="source-label">{t("adminI18n.sourceLocale")}</span>
				<strong>{data?.sourceLocale.toUpperCase()}</strong>
				<span className="source-count">
					{t("adminI18n.keysInCatalog", { count: data?.sourceKeyCount ?? 0 })}
				</span>
			</div>

			{actionError && <div className="error-message">{actionError}</div>}

			<form className="add-locale-form" onSubmit={handleAddLocale}>
				<input
					type="text"
					value={newLocale}
					onChange={(event) => setNewLocale(event.target.value)}
					placeholder={t("adminI18n.addLocalePlaceholder")}
					aria-label={t("adminI18n.addLocale")}
				/>
				<button type="submit" className="btn btn-primary" disabled={!newLocale.trim()}>
					{t("adminI18n.addLocaleSubmit")}
				</button>
			</form>

			<div className="locales-list">
				{data?.locales.map((locale) => (
					<LocaleCard
						key={locale.locale}
						locale={locale}
						isBusy={busyLocale === locale.locale}
						isSuperAdmin={isSuperAdmin}
						onRetranslate={handleRetranslate}
						onRequestDelete={() => setPendingDeletion(locale.locale)}
					/>
				))}
			</div>

			{pendingDeletion && (
				<ConfirmDialog
					title={t("adminI18n.deleteLocaleTitle")}
					message={t("adminI18n.deleteLocaleBody", { locale: pendingDeletion })}
					confirmLabel={t("adminI18n.deleteLocaleConfirm")}
					cancelLabel={t("adminI18n.cancelEdit")}
					isDestructive
					onConfirm={handleDelete}
					onCancel={() => setPendingDeletion(null)}
				/>
			)}
		</div>
	);
}

function LocaleCard({
	locale,
	isBusy,
	isSuperAdmin,
	onRetranslate,
	onRequestDelete,
}: {
	locale: TranslationLocaleSummary;
	isBusy: boolean;
	isSuperAdmin: boolean;
	onRetranslate: (locale: string, force: boolean) => void;
	onRequestDelete: () => void;
}) {
	const { t } = useI18n();
	const isTranslating = locale.status === "translating";
	const needsAttention = locale.counts.missing + locale.counts.rejected + locale.counts.stale;

	return (
		<div className="locale-card">
			<div className="locale-card-head">
				<div>
					<h2>
						<Link to={`/translations/${locale.locale}`}>{locale.label}</Link>
						<span className="locale-code">{locale.locale}</span>
					</h2>
					<div className="locale-badges">
						<span className={`status-badge ${locale.status}`}>{locale.status}</span>
						{locale.outdated && (
							<span className="status-badge outdated">{t("adminI18n.outdatedBadge")}</span>
						)}
						<span className="locale-version">v{locale.version}</span>
					</div>
				</div>
				<div className="locale-actions">
					<button
						type="button"
						className="btn btn-secondary"
						onClick={() => onRetranslate(locale.locale, false)}
						disabled={isBusy || isTranslating}
					>
						{t("adminI18n.retranslate")}
					</button>
					<button
						type="button"
						className="btn btn-secondary"
						onClick={() => onRetranslate(locale.locale, true)}
						disabled={isBusy || isTranslating}
					>
						{t("adminI18n.retranslateAll")}
					</button>
					<button
						type="button"
						className="btn btn-danger"
						onClick={onRequestDelete}
						disabled={!isSuperAdmin}
						title={isSuperAdmin ? undefined : t("adminI18n.superAdminOnly")}
					>
						{t("adminI18n.deleteLocale")}
					</button>
				</div>
			</div>

			{isTranslating && <p className="locale-running">{t("adminI18n.retranslateStarted")}</p>}
			{locale.lastError && <p className="locale-error">{locale.lastError.message}</p>}

			<dl className="locale-counts">
				<CountItem label={t("adminI18n.translated")} value={locale.counts.translated} />
				<CountItem label={t("adminI18n.missing")} value={locale.counts.missing} warn />
				<CountItem label={t("adminI18n.rejected")} value={locale.counts.rejected} warn />
				<CountItem label={t("adminI18n.override")} value={locale.counts.override} />
				<CountItem label={t("adminI18n.stale")} value={locale.counts.stale} warn />
			</dl>

			{needsAttention > 0 && (
				<Link className="locale-attention" to={`/translations/${locale.locale}?filter=missing`}>
					{t("adminI18n.keysCount", { count: needsAttention })}
				</Link>
			)}
		</div>
	);
}

function CountItem({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
	return (
		<div className={`count-item ${warn && value > 0 ? "warn" : ""}`}>
			<dt>{label}</dt>
			<dd>{value}</dd>
		</div>
	);
}
